export const colors = {
    brandPrimary: '#6466E9',
    brandLight: '#E8E8FC',
    brandDark: '#4F51D2',
    textPrimary: '#59606A',
    textSecondary: '#939AA4',
    textMuted: '#C0C8D1',
    white: '#ffffff',
    black: '#111111',
    border: '#e7e8ea',
    danger: '#F54848',
    dangerSecondary: '#FFE8E8',
    success: '#52CB50',
    successSecondary: '#E2FFD7',
    transparent: 'transparent',
    background: '#F6F6F6',
    red: '#FF3030',
    lightGrey: '#F7F7F7',
    darkGrey: '#1D2229',
    warnings: '#FF4B55',
    textGrey1: '#222222',
    textGrey2: '#474D55',
};

export const font = {
    family: {
        primary: 'Inter, sans-serif',
    },
};

export const border = {
    radius: '8px',
};

export const transition = {
    duration: '200ms',
    func: 'ease',
};

export const breakpoint = {
    phone: '500px',
    tablet: '768px',
    desktop: '1170px',
};

export const buttonReset = `
    appearance: none;
    padding: 0;
    margin: 0;
    border: 0;
    outline: none;
    font: inherit;
    color: inherit;
    background-color: transparent;
    cursor: pointer;
    user-select: none;
    overflow: visible;
    
    &:-moz-focusring {
        outline: none;
    }
    
    &::-moz-focus-inner {
        border: 0;
        padding: 0;
    }
`;

export const textEllipsis = `
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export type InputFieldBaseType = 'normal' | 'disabled' | 'error';
const getTypeStyles = (type: InputFieldBaseType) => {
    switch (type) {
        case 'disabled':
            return `
                border-color: ${colors.border};
                background: ${colors.white};
                color: ${colors.textMuted};
            `;
        case 'error':
            return `
                border-color: ${colors.danger};
                background: #FFF2F2;
                color: ${colors.textPrimary};
            `;
        default:
            return `
                border-color: ${colors.border};
                background: ${colors.white};
                color: ${colors.textPrimary};
                
                &:hover {
                    border-color: ${colors.brandPrimary};
                }
                
                &:focus {
                    border-color: ${colors.brandPrimary};
                }
            `;
    }
};
export const inputFieldBase = (type: InputFieldBaseType, size: 'sm' | 'md' = 'md') => `
    font-family: ${font.family.primary};
    font-style: normal;
    font-weight: normal;
    font-size: 12px;
    line-height: 12px;
    height: ${size === 'md' ? '44px' : '36px'};
    
    padding: 0 16px;
    
    background: ${colors.white};
    border: 1px solid ${colors.border};
    border-radius: ${border.radius};
    
    &:placeholder {
        color: ${colors.border};
    }
    
    &:focus {
        outline: none;
        box-shadow: none;
    }
    
    transition: all ${transition.duration};
    
    ${getTypeStyles(type)};
`;

export const icons = {
    copyToClipboard:
        "data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10.5859 20H3.125C1.40182 20 0 18.5982 0 16.875V6.28906C0 4.56589 1.40182 3.16406 3.125 3.16406H10.5859C12.3091 3.16406 13.7109 4.56589 13.7109 6.28906V16.875C13.7109 18.5982 12.3091 20 10.5859 20ZM3.125 4.72656C2.26349 4.72656 1.5625 5.42755 1.5625 6.28906V16.875C1.5625 17.7365 2.26349 18.4375 3.125 18.4375H10.5859C11.4474 18.4375 12.1484 17.7365 12.1484 16.875V6.28906C12.1484 5.42755 11.4474 4.72656 10.5859 4.72656H3.125ZM16.8359 14.9219V3.125C16.8359 1.40182 15.4341 0 13.7109 0H5.03906C4.60754 0 4.25781 0.349731 4.25781 0.78125C4.25781 1.21277 4.60754 1.5625 5.03906 1.5625H13.7109C14.5724 1.5625 15.2734 2.26349 15.2734 3.125V14.9219C15.2734 15.3534 15.6232 15.7031 16.0547 15.7031C16.4862 15.7031 16.8359 15.3534 16.8359 14.9219Z' fill='%23939AA4'/%3E%3C/svg%3E",
    download:
        "data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M14 10L14 12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14L3.33333 14C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667L2 10' stroke='%236466E9' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M4.66536 6.6665L7.9987 9.99984L11.332 6.6665' stroke='%236466E9' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M8 10L8 2' stroke='%236466E9' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E",
    reject:
        "data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12.6654 3.33301L3.33203 12.6663M3.33203 3.33301L12.6654 12.6663' stroke='%23FF4B55' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A",
    accept:
        "data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6.6727 11.3337C6.48594 11.3337 6.30852 11.2592 6.17779 11.129L3.53513 8.49678C3.26433 8.22704 3.26433 7.78058 3.53513 7.51085C3.80593 7.24111 4.25416 7.24111 4.52496 7.51085L6.6727 9.65014L11.4724 4.86929C11.7432 4.59956 12.1915 4.59956 12.4623 4.86929C12.7331 5.13903 12.7331 5.58549 12.4623 5.85523L7.16761 11.129C7.03688 11.2592 6.85946 11.3337 6.6727 11.3337Z' fill='white'/%3E%3C/svg%3E%0A",
    close:
        "data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12.668 12.6668L3.33464 3.3335M12.668 3.3335L3.33464 12.6668' stroke='%236466E9' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E",
};
